import {FC} from 'react';
import {ui} from 'store';
import {select} from 'store/helpers';
import {Activity, Entity, Goal, GoalProject} from 'types';
import {details} from '../selectors/goal';
import {DropCallback, generate} from 'helpers';
import {progressIndex} from '../selectors/progress';

export const attach = (({currentTarget}, {id}) => ({
  type: 'GOALS_ATTACH_MILESTONE' as const,
  source_id: id,
  goal_id: currentTarget.dataset.id
})) satisfies DropCallback;

export const attachToProject = (({currentTarget}, {id}) => ({
  type: 'GOALS_ATTACH_TO_PROJECT' as const,
  source_id: id,
  target_id: currentTarget.dataset.id
})) satisfies DropCallback;

export const duplicate = ({id, duplicateId = generate(), activities = [], milestones = []}: Entity & {duplicateId?: string,activities?: Activity[], milestones?: Goal[]}) => {
  return {
    type: 'GOALS_DUPLICATE' as const,
    targetId: id,
    duplicateId,
    activities,
    milestones,
    url: `/goals/${duplicateId}/`
  }
};

export const duplicateWeeklyObjectiveWithMilestonesAndTasks = ({id, duplicateId = generate(), activities = [], milestones = []}: Entity & {duplicateId?: string,activities?: Activity[], milestones?: Goal[]}) => {
  return {
    type: 'GOALS_DUPLICATE' as const,
    targetId: id,
    duplicateId,
    activities,
    milestones,
    url: `/goals/weekly-objectives/${duplicateId}/`
  }
};

export const duplicatePlanMyWeekObjective = ({id, duplicateId = generate(), activities = [], milestones = []}: Entity & {duplicateId?: string,activities?: Activity[], milestones?: Goal[]}) => {
  return {
    type: 'GOALS_DUPLICATE' as const,
    targetId: id,
    duplicateId,
    activities,
    milestones,
    url: `/today/plan-my-week/start`
  }
};

export const smartDuplicate = ({id}: Entity, DuplicateMenu?: FC) => {
  const {activities, milestones} = select(details, {id, context: 'goals'});
  if (activities.length || milestones.length) {
    return ui.popup.redirect(DuplicateMenu);
  }
  return duplicate({id});
};

export const smartDuplicateWeeklyObjective = ({id}: Entity, DuplicateMenu?: FC) => {
  const {activities, milestones} = select(details, {id, context: 'goals'});
  if (activities.length || milestones.length) {
    return ui.popup.redirect(DuplicateMenu);
  }
  return duplicateWeeklyObjectiveWithMilestonesAndTasks({id});
};

export const smartDuplicatePlanMyWeekObjective = ({id}: Entity, DuplicateMenu?: FC) => {
  const {activities, milestones} = select(details, {id, context: 'goals'});
  if (activities.length || milestones.length) {
    return ui.popup.redirect(DuplicateMenu);
  }
  return duplicatePlanMyWeekObjective({id});
};

export const upsert = (goal: Partial<Goal>) => ({
  type: 'GOALS_UPDATE' as const,
  goal: {
    ...goal,
    id: goal.id || generate()
  }
});

export const remove = ({id}: Entity) => ({
  type: 'GOALS_DELETE' as const,
  id,
  progressIndex: select(progressIndex),
  ...select(details, {id, context: 'goals'}),
});

export const removeProject = ({id}: Entity) => ({
  type: 'GOALS_DELETE_PROJECT' as const,
  id
});

export const toggle = ({id}: Entity) => ({
  type: 'GOALS_TOGGLE' as const,
  id
});

export const hold = ({id}: Entity) => ({
  type: 'GOALS_HOLD' as const,
  id
});

export const upsertProject = (project: GoalProject) => {
  const id = generate();
  return {
    type: 'GOALS_SET_PROJECT' as const,
    project: ({
      id: project.id ?? id,
      name: project.name,
      note: project.note,
    }),
    notification: !project.id && 'New project has been created!',
    url: project.id ? undefined : `/goals/projects/${id}/`
  }
};

export const updateProgress = (value: string, name: string) => {
  const [goalId, ts] = name.split('_');
  return {
    type: 'GOALS_SET_PROGRESS' as const,
    goalId,
    ts,
    value,
    progressIndex: select(progressIndex)
  };
};
